/**
 * useStatusSummary — a single polling hook that replaces individual per-badge
 * polling hooks in the navigation layer.
 *
 * Design decisions:
 * - Uses React Query so the network request is deduplicated across all callers:
 *   MainLayout + NotificationBell both call this but only ONE fetch fires.
 * - Page Visibility API: `refetchIntervalInBackground: false` pauses polling
 *   entirely when the tab is hidden; resumes (and refetches) on tab focus.
 * - Feature-flag fallback: when the tenant has `unified_badge_poll_disabled`
 *   enabled, this hook returns all-zeroes and the individual per-badge polls
 *   in MainLayout take over. This lets ops disable the unified endpoint
 *   per-tenant without a code deploy.
 * - Backoff: React Query's built-in retry/error state is surfaced via
 *   `isError`; callers can show stale data while the server recovers.
 *   On 429 the fetchFn re-throws, triggering React Query's retry backoff.
 */
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { customFetch } from "@workspace/api-client-react";
import { useMyFeatures } from "@/lib/auth";
import { useJitteredInterval } from "@/lib/jitter";

// ── Shared query key ──────────────────────────────────────────────────────────
// Exported so mutations elsewhere (mark-all-read, approve/reject social post,
// etc.) can call queryClient.invalidateQueries({ queryKey: STATUS_SUMMARY_KEY })
// to trigger an immediate refresh without waiting for the next poll interval.
export const STATUS_SUMMARY_QUERY_KEY = ["/api/ui/status-summary"] as const;

// ── Response shape ─────────────────────────────────────────────────────────────
export interface StatusSummaryData {
  generatedAt: string;
  unreadAlerts: number;
  /** Unread eBay/TikTok messages visible to the current operator. */
  unreadMessages?: number;
  pendingSocialApprovals?: number;
  unreadSocialInbox?: number;
  pendingPacking?: number;
  openReturns?: number;
  overdueTasks?: number;
}

// ── Hook ──────────────────────────────────────────────────────────────────────

/**
 * Returns the current navigation badge counts from the unified
 * `GET /api/ui/status-summary` endpoint.
 *
 * Polling is paused when the tab is hidden (Page Visibility API) and resumed
 * on tab focus.  When `unified_badge_poll_disabled` is set for the tenant,
 * the hook is disabled entirely and individual per-badge polls take over.
 *
 * @param enablePolling - Pass `false` to read from the React Query cache
 *   without starting/contributing to the polling interval.  Useful for
 *   components that should read summary data but should NOT be the ones
 *   driving the refetch timer (e.g. NotificationBell reads the same data
 *   as MainLayout but doesn't need its own interval).
 * @param enabled - Whether this observer may start or refetch the request.
 *   Pass `false` for cache-only observers or while a primary route is loading.
 */
export function useStatusSummary(enablePolling = true, enabled = true) {
  const { hasFeature, isLoading: featLoading } = useMyFeatures();
  // Fallback flag: when set, individual polls handle badges and this hook is off.
  const isDisabled = !featLoading && hasFeature("unified_badge_poll_disabled");

  // Jittered 60 s interval — same jitter as the previous per-badge polls to
  // avoid all instances snapping to the same second after a tab restore.
  const pollInterval = useJitteredInterval(60_000);

  const query = useQuery<StatusSummaryData>({
    queryKey: STATUS_SUMMARY_QUERY_KEY,
    queryFn: async () => {
      const res = await customFetch<StatusSummaryData>("/api/ui/status-summary");
      return res;
    },
    enabled: enabled && !isDisabled,
    // Polling: active only when `enablePolling` is true AND the tab is visible.
    refetchInterval: enablePolling && enabled && !isDisabled ? pollInterval : false,
    // Stop polling entirely when the tab is hidden; React Query resumes + refetches
    // on the next visibility-change event (equivalent to Page Visibility API pause).
    refetchIntervalInBackground: false,
    // Refresh on window focus so the badge is up-to-date after the user returns.
    refetchOnWindowFocus: true,
    // staleTime matches the server-side cache TTL so a fresh response isn't
    // immediately re-fetched when another component mounts with the same key.
    staleTime: 30_000,
  });

  const data = query.data;
  return {
    /** Unread alert count — always present (0 on error/loading). */
    unreadAlerts: data?.unreadAlerts ?? 0,
    /** Unread marketplace messages — omitted when integrations are not visible. */
    unreadMessages: data?.unreadMessages,
    /** Pending social-post approvals — undefined when feature/perm not available. */
    pendingSocialApprovals: data?.pendingSocialApprovals,
    /** Unread social inbox count — undefined when feature/perm not available. */
    unreadSocialInbox: data?.unreadSocialInbox,
    /** Active packing jobs — undefined when module/perm not available. */
    pendingPacking: data?.pendingPacking,
    /** Open returns — undefined when module/perm not available. */
    openReturns: data?.openReturns,
    /** Overdue tasks for current user — undefined when module/perm not available. */
    overdueTasks: data?.overdueTasks,
    /** True while the feature flag check hasn't resolved yet or the first fetch
     *  hasn't returned. Consumers can show placeholder badges. */
    isLoading: query.isLoading || featLoading,
    /** True when the most recent fetch failed (e.g. server returned 5xx/429).
     *  Consumers may show stale counts until the next successful poll. */
    isError: query.isError,
    /** True when the `unified_badge_poll_disabled` flag is set — the hook is a
     *  no-op and individual polls should be active. */
    isDisabled,
  };
}

// ── Invalidation helper ───────────────────────────────────────────────────────

/**
 * Returns a function that triggers an immediate status-summary refresh.
 * Call it from mutation `onSuccess` handlers where a badge count changed
 * (e.g. mark-all-read, approve a social post, close a return).
 *
 * Usage:
 *   const invalidateSummary = useInvalidateStatusSummary();
 *   useMarkAllAlertsRead({ mutation: { onSuccess: invalidateSummary } });
 */
export function useInvalidateStatusSummary() {
  const qc = useQueryClient();
  return () => void qc.invalidateQueries({ queryKey: STATUS_SUMMARY_QUERY_KEY });
}
